import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, FileSearch, AlertCircle } from 'lucide-react';
import PageHeader from '../../../components/ui/PageHeader';
import Skeleton from '../../../components/ui/Skeleton';
import ItemRow from '../../../components/dailyAudit/ItemRow';
import EditCheckModal from '../../../components/dailyAudit/EditCheckModal';
import { useShop } from '../../../hooks/useShop';
import { api } from '../../../api/client';

const TABS = [
  { key: 'needs_action', label: 'Needs Action', filter: 'AUDIT_FLAGGED,AWAITING_VENDOR_PROFILE' },
  { key: 'passed', label: 'Passed Audit', filter: 'AUDIT_PASSED' },
  { key: 'all', label: 'All', filter: 'AUDIT_FLAGGED,AWAITING_VENDOR_PROFILE,AWAITING_DOC_REVIEW,AUDIT_PASSED,IGNORED,PENDING_REVIEW' },
];

export default function DailyAuditPathwayPage() {
  const { date } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentShopId, currentShop } = useShop();

  const tabKey = searchParams.get('tab') || 'needs_action';
  const tab = TABS.find(t => t.key === tabKey) || TABS[0];

  const [pathwaySummary, setPathwaySummary] = useState(null);
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);  // check being edited
  const [refreshNonce, setRefreshNonce] = useState(0);

  // Fetch the pathway summary for the gate decision + missing-scan banner
  useEffect(() => {
    if (!currentShopId || !date) return;
    let cancelled = false;
    api.dailyAuditPathwaySummary(date, 'cod_checks', currentShopId)
      .then(d => { if (!cancelled) setPathwaySummary(d); })
      .catch(e => { if (!cancelled) setError(e.message || String(e)); });
    return () => { cancelled = true; };
  }, [currentShopId, date, refreshNonce]);

  // Fetch checks for the current sub-tab
  useEffect(() => {
    if (!currentShopId || !date) return;
    let cancelled = false;
    setLoading(true);
    api.listCodChecks({ shopId: currentShopId, status: tab.filter, folderDate: date })
      .then(d => { if (!cancelled) { setChecks(d.checks || []); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message || String(e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [currentShopId, date, tab.filter, refreshNonce]);

  const reload = useCallback(() => setRefreshNonce(n => n + 1), []);

  const handleMarkPassed = async (check) => {
    try {
      await api.setCodCheckStatus(check.id, 'AUDIT_PASSED');
      reload();
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  const handleMarkIgnored = async (check) => {
    if (!confirm('Mark this check as ignored? It will be moved to the Ignored bucket.')) return;
    try {
      await api.setCodCheckStatus(check.id, 'IGNORED');
      reload();
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  const dateObj = new Date(date + 'T00:00:00');
  const human = dateObj.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  // Hard gate: if the pathway has open review items, redirect to the review queue
  if (pathwaySummary?.gate_open) {
    return <Navigate to={`/auditor/daily-audit/${date}/cod-checks/review`} replace />;
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={[
          { label: 'Daily Audit', href: '/auditor/daily-audit' },
          { label: human, href: `/auditor/daily-audit/${date}` },
          { label: 'COD Checks' },
        ]}
        title={`COD Checks — ${human}`}
        actions={
          <Link
            to={`/auditor/daily-audit/${date}`}
            className="text-[13px] text-[var(--color-warm-600)] hover:text-[var(--color-warm-900)] flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to pathways
          </Link>
        }
      />

      {/* Missing-scan banner */}
      {pathwaySummary?.missing_scan_state === 'no_scan_yet' && (
        <MissingScanBanner
          title="Scans missing!"
          subtitle="No COD pipeline scan has been run for this date yet. The nightly job runs at 3:30 AM."
        />
      )}
      {pathwaySummary?.missing_scan_state === 'folder_empty' && (
        <MissingScanBanner
          title="Scans missing!"
          subtitle="The Drive folder for this date exists but had no invoice PDFs. Ask the store to upload."
        />
      )}
      {pathwaySummary?.missing_scan_state === 'scan_errored' && (
        <MissingScanBanner
          title="Scan errored"
          subtitle={pathwaySummary?.scan_meta?.errors || 'The pipeline ran but reported errors.'}
        />
      )}

      {/* Sub-tabs */}
      <div className="border-b border-[var(--color-warm-200)] mb-4">
        <div className="flex items-center gap-1">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setSearchParams({ tab: t.key })}
              className={`px-4 py-2 text-[13px] font-semibold border-b-2 transition-colors ${
                tab.key === t.key
                  ? 'border-[var(--color-warm-800)] text-[var(--color-warm-900)]'
                  : 'border-transparent text-[var(--color-warm-500)] hover:text-[var(--color-warm-700)]'
              }`}
            >
              {t.label}
              {pathwaySummary && (
                <span className="ml-2 text-[11px] text-[var(--color-warm-400)]">
                  {t.key === 'needs_action' ? pathwaySummary.needs_action :
                   t.key === 'passed' ? pathwaySummary.passed :
                   pathwaySummary.total}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-lg" />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="rounded-xl border border-[var(--color-danger-300)] bg-[var(--color-danger-50)] p-4 text-[var(--color-danger-700)] text-[14px]">
          Failed to load: {error}
        </div>
      )}

      {!loading && !error && checks.length === 0 && (
        <div className="rounded-xl border border-[var(--color-warm-200)] bg-white p-8 text-center">
          <FileSearch className="w-10 h-10 text-[var(--color-warm-400)] mx-auto mb-3" />
          <p className="text-[14px] text-[var(--color-warm-700)]">
            {tab.key === 'needs_action' && 'Nothing needs action for this date. Nice.'}
            {tab.key === 'passed' && 'No items have been passed yet.'}
            {tab.key === 'all' && 'No items found for this date.'}
          </p>
        </div>
      )}

      {!loading && !error && checks.length > 0 && (
        <div className="space-y-3">
          {checks.map(c => (
            <ItemRow
              key={c.id}
              check={c}
              onEdit={() => setEditing(c)}
              onMarkPassed={handleMarkPassed}
              onMarkIgnored={handleMarkIgnored}
            />
          ))}
        </div>
      )}

      <EditCheckModal
        open={!!editing}
        check={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); reload(); }}
      />
    </div>
  );
}

function MissingScanBanner({ title, subtitle }) {
  return (
    <div className="rounded-xl border border-[var(--color-danger-300)] bg-[var(--color-danger-50)] p-4 mb-4 flex items-start gap-3">
      <AlertCircle className="w-5 h-5 text-[var(--color-danger-700)] shrink-0 mt-0.5" />
      <div>
        <h3 className="text-[14px] font-bold text-[var(--color-danger-700)]">{title}</h3>
        <p className="text-[13px] text-[var(--color-danger-700)] mt-0.5">{subtitle}</p>
      </div>
    </div>
  );
}
